import type { GetServerSideProps, NextPage } from 'next'
import { fillPromotionResults, getProductBySku, getProductByUrl, getProductReviews } from '@api/products'
import { getParsedCustom8OrEmptyObject } from '@utils/product-data'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS, KK_LOCALE_MAP } from '@model/locales'
import type { Product } from '@model/product'
import { logError } from '@utils/errors-client'
import { SESSION_ID_COOKIE } from '@pages/api/auth'
import { getAllPromotions, getCurrencies } from '@api/misc'
import { getPrices } from '@utils/product/product-info'
import type { CurrenciesInfo } from '@hooks/useCurrency'
import { CURRENCY_MAP, getCurrencyOptByLanguageId } from '@hooks/useCurrency'
import { propEq } from 'ramda'
import { unSlug } from '@utils/slugs'
import { getBestPrice } from '@api/ecw/price'
import { KK_STORE } from '@api/apiConfig'
import type { KK_STORE_TYPE } from '@pages/api/proxy'
import type { Catalog } from '@model/pricelist/Catalog'
import { isPromotionValidForCatalog } from '@utils/promotion'
import { parseDescriptionWithBreadCrumbs } from '@utils/parseDescriptionWithBreadCrumbs'
import { truncateText } from '@utils/truncateText'
import { getProductTitle } from '@utils/getProductTitle'

import type { ProductViewProps } from '@components/product/ProductView/ProductView'
import { Helmet } from '@components/helmet'
import type { HelmetProps } from '@components/helmet/Helmet'
import ProductPage from '../../components/pages/ProductPage'
import { checkCatalog } from '@utils/check-catalog'

export interface ProductPageWrapperProps extends ProductViewProps {
  helmet: HelmetProps
}
const ProductPageWrapper: NextPage<ProductPageWrapperProps> = ({
  helmet,
  product,
  reviews,
  recommendations,
  recentlyViewedProducts,
}) => {
  return (
    <>
      <Helmet {...helmet} />

      <ProductPage {...{ product, reviews, recommendations, recentlyViewedProducts }} />
    </>
  )
}

export default ProductPageWrapper

export const getServerSideProps: GetServerSideProps = async ({ params, locale, req }) => {
  const { slug, catalogId = '' } = params as { slug: string; catalogId: string }
  const languageId = KK_LANG_IDS[locale as Locale]
  const sessionId = req.cookies[SESSION_ID_COOKIE]
  const sku = unSlug(slug)
  const catalog = await checkCatalog(locale as Locale, catalogId)

  if (!catalog) {
    return {
      notFound: true,
    }
  }

  try {
    const product: Product = await getProductBySku(sku, languageId, catalogId ? (catalogId as Catalog) : undefined)

    if (KK_STORE === 'store1' && product.url && catalogId) {
      const productByUrl = await getProductByUrl(product.url, languageId, catalogId as Catalog)
      product.price0 = productByUrl.price0 || 0
      product.price2 = productByUrl.price2 || 0
    }

    const promotions = await getAllPromotions(sessionId, languageId)
    const { promotionResults = [] } = await fillPromotionResults([product], languageId, promotions)

    const freeProductPromo = promotions.find(propEq('orderTotalCode', 'ot_free_product'))

    product.promotionResults = promotionResults.map((promo) => ({
      ...promo,
      ...(freeProductPromo && { promotionName: freeProductPromo.name, description: freeProductPromo.description }),
    }))

    if (!product) throw new Error(`Cannot get product by sku ${sku}`)

    const reviews = await getProductReviews(sku, languageId)
    const currencies: CurrenciesInfo[] = await getCurrencies(languageId)
    const currencyOpt = getCurrencyOptByLanguageId(languageId, currencies)

    try {
      const filteredPromotions = promotions.filter(isPromotionValidForCatalog((catalogId as Catalog) || ''))
      product.priceInfo = getPrices(
        product,
        KK_STORE as KK_STORE_TYPE,
        locale as string,
        filteredPromotions,
        currencyOpt,
        '1'
      )
      product.variantInfo = getParsedCustom8OrEmptyObject(product)
      product.productPromotion = filteredPromotions
      if (KK_STORE === 'store1' && product.priceInfo && product.priceInfo.bundleIncludedProducts?.length) {
        const productPromises = product.priceInfo.bundleIncludedProducts.map(
          (product): Promise<Product> => getProductByUrl(product.url, 1, catalogId as Catalog)
        )
        product.priceInfo.bundleIncludedProducts = await Promise.all(productPromises)
      }

      if (KK_STORE === 'store2') {
        product.ecwPrice = await getBestPrice(product.custom2Int, product.sku)
      }
    } catch (e) {
      logError(`Error on server side for ProductPage, sku=${sku}`, e, true)
    }

    const protocol = req.headers['x-forwarded-proto'] || 'https'

    const host = req.headers.host
    const baseUrl = `${protocol}://${host}`

    const description = product.description
      ? parseDescriptionWithBreadCrumbs(product.description).description
      : product.name?.trim()
    const descriptionText = truncateText(description, 155)

    const imageUrlLoader = ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
      return `${src}&w=${width}&q=${quality || 75}`
    }
    const imagesBase = `${process.env.KK_HOST}/images`
    const generatedImageUrl = `${baseUrl}/_next/image?url=${imageUrlLoader({
      src: `${imagesBase}/${product.image}`,
      width: 3840,
      quality: 100,
    })}`

    const helmet = {
      title: `${product.manufacturerName} - ${getProductTitle(product)}`,
      description: descriptionText,
      type: 'product',
      imageUrl: generatedImageUrl,
      product: product,
      url: `${baseUrl}/${locale}/product/${slug}`,
      locale: KK_LOCALE_MAP[locale as Locale],
      sku: product.sku,
      price: product.priceInfo.topPrice.toString(),
      currency: CURRENCY_MAP[locale as Locale],
      brand: product.manufacturerName,
      stock: product.status === 0 ? 'out stock' : 'in stock',
      condition: 'new',
    }

    return {
      props: {
        product,
        reviews,
        recommendations: [],
        recentlyViewedProducts: [],
        helmet,
      },
    }
  } catch (e) {
    logError(`Error on server side for ProductPage, sku=${sku}`, e, true)
    return {
      notFound: true,
    }
  }
}
